import { useState } from 'react'
import { Button, Input, Label, Paragraph } from '@quickmd-llc/design-system'
import type {
  PharmacyOrderShippingAddressDoctorApiOutContract,
  ShipToAddressOverrideStaffApiInContract,
} from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'

const DELIVERY_INSTRUCTIONS_MAX = 500

type Props = {
  initial?: PharmacyOrderShippingAddressDoctorApiOutContract | null
  onSubmit: (override: ShipToAddressOverrideStaffApiInContract) => void
  onCancel: () => void
  pending?: boolean | undefined
  error?: string | null | undefined
}

function text(value: string | null | undefined): string {
  return value ?? ''
}

// Editable delivery-address fields for an order-scoped ship-to override. State + country are
// inherited from the order and intentionally NOT editable here (the rates API ignores them).
export function AddressForm({ initial, onSubmit, onCancel, pending, error }: Props) {
  const [address1, setAddress1] = useState(text(initial?.Address1))
  const [address2, setAddress2] = useState(text(initial?.Address2))
  const [city, setCity] = useState(text(initial?.City))
  const [zip, setZip] = useState(text(initial?.Zip))
  const [deliveryInstructions, setDeliveryInstructions] = useState(text(initial?.DeliveryInstructions))

  const canSave = address1.trim() !== '' && city.trim() !== '' && zip.trim() !== ''

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!canSave) return
    onSubmit({
      Address1: address1.trim(),
      Address2: address2.trim() || null,
      City: city.trim(),
      Zip: zip.trim(),
      DeliveryInstructions: deliveryInstructions.trim() || null,
    })
  }

  return (
    <form className='grid gap-sp-2' onSubmit={handleSubmit}>
      <div className='grid gap-sp-1'>
        <Label size='sm' className='text-text-light'>
          Street address
        </Label>
        <Input value={address1} onChange={(event) => setAddress1(event.target.value)} placeholder='Street address' />
        <Input
          value={address2}
          onChange={(event) => setAddress2(event.target.value)}
          placeholder='Apt, suite, unit (optional)'
        />
      </div>

      <div className='flex gap-sp-2'>
        <div className='grid flex-1 gap-sp-1'>
          <Label size='sm' className='text-text-light'>
            City
          </Label>
          <Input value={city} onChange={(event) => setCity(event.target.value)} />
        </div>
        <div className='grid w-[110px] gap-sp-1'>
          <Label size='sm' className='text-text-light'>
            ZIP
          </Label>
          <Input value={zip} inputMode='numeric' onChange={(event) => setZip(event.target.value)} />
        </div>
      </div>

      <div className='grid gap-sp-1'>
        <Label size='sm' className='text-text-light'>
          Delivery instructions (optional)
        </Label>
        <Input
          value={deliveryInstructions}
          onChange={(event) => setDeliveryInstructions(event.target.value.slice(0, DELIVERY_INSTRUCTIONS_MAX))}
          placeholder='e.g. Leave at back door'
        />
        {deliveryInstructions.length >= DELIVERY_INSTRUCTIONS_MAX ?
          <Label size='sm' className='text-text-light'>
            {deliveryInstructions.length}/{DELIVERY_INSTRUCTIONS_MAX}
          </Label>
        : null}
      </div>

      <div className='flex items-center justify-end gap-sp-1'>
        <Button type='button' priority='tertiary' size='s' onClick={onCancel} disabled={!!pending}>
          Cancel
        </Button>
        <Button type='submit' priority='primary' size='s' disabled={!canSave || !!pending} loading={!!pending}>
          Save address
        </Button>
      </div>

      {error ?
        <Paragraph size='sm' error role='alert'>
          {error}
        </Paragraph>
      : null}

      <Label size='sm' className='text-text-light'>
        Updates this order only — not the patient&apos;s account. State and country stay as set on the order.
      </Label>
    </form>
  )
}
